// Concierge travel (CHANGE-travel): points pay for trips ernd books by hand, at 100 points = $1.
// One idea per screen: the list of trips, the request form, one trip with its quote.
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents, day, points, useCurrentCompany, useMe } from '../../lib'
import { Empty, ErrorBox, Loading, NoCompany } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import type { Trip, TripStatus } from './types'

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '')

export const TRIP_STATE: Record<TripStatus, string> = {
  requested: 'Waiting for a quote',
  quoted: 'Quote ready',
  accepted: 'Booking',
  booked: 'Booked',
  canceled: 'Canceled',
  declined: 'Declined',
  refunded: 'Refunded',
}

/** "50,000" / "50000" → 50000; anything else → null. No float math. */
function wholePoints(s: string): number | null {
  const t = s.trim().replace(/,/g, '')
  return /^[1-9]\d{0,11}$/.test(t) ? Number(t) : null
}

function dates(t: Trip): string {
  return t.return_on ? `${day(t.depart_on)} to ${day(t.return_on)}` : day(t.depart_on)
}

/** Travel is behind TRAVEL_ENABLED; when it is off, its pages say so and do nothing else. */
function useTravelOn(): boolean | undefined {
  const { me } = useMe()
  return me ? me.features?.travel === true : undefined
}

function TravelOff() {
  return <div className="page stack" data-testid="travel-off"><PageBand title="Travel" sub="Travel is not available right now." /></div>
}

export function TravelPage() {
  const on = useTravelOn()
  const company = useCurrentCompany()
  const list = useQuery({
    queryKey: ['travel', 'list', company?.id],
    queryFn: () => api<{ rows: Trip[]; ready_points: number }>('GET', `/api/companies/${company!.id}/trips`),
    enabled: !!company && on === true,
    refetchInterval: 10000,
  })
  if (on === false) return <TravelOff />
  if (!company) return <NoCompany />
  const rows = list.data?.rows ?? []
  return (
    <div className="page stack" data-testid="travel-page">
      <PageBand
        title="Travel"
        sub="Flights and hotels ernd books by hand, with a quote first. 100 points = $1 of what the trip costs."
        fine={list.data ? <span data-testid="travel-ready">{points(list.data.ready_points)} points ready</span> : undefined}
        action={<Link className="e-btn e-btn-primary" to="/travel/new" data-testid="travel-new">Ask for a trip</Link>}
      />
      {list.isLoading && <Loading />}
      {list.error && <ErrorBox error={list.error} />}
      {!list.isLoading && rows.length === 0 && <Empty>No trips yet.</Empty>}
      {rows.length > 0 && (
        <section className="e-panel e-rows">
          <h2 className="e-heading">Trips</h2>
          <ul>
            {rows.map((t) => (
              <li key={t.id} data-testid="travel-row" data-id={t.id}>
                <span className="e-row-name">
                  <Link to={`/travel/${t.id}`}>{t.destination}</Link>
                  <span className="e-row-note">{dates(t)}, {t.travelers} {t.travelers === 1 ? 'traveler' : 'travelers'}</span>
                </span>
                <span className="e-trip-right">
                  <span className="num">{t.points_taken !== null ? `${points(t.points_taken - t.refund_points)} points` : t.quote_points !== null ? `${points(t.quote_points)} points` : `Up to ${points(t.max_points)} points`}</span>
                  <span className="e-row-note" data-testid="travel-state" data-status={t.status}>{TRIP_STATE[t.status] ?? t.status}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <p className="e-back"><Link className="e-link" to="/redeem">Back to Redeem</Link></p>
    </div>
  )
}

const KIND_TEXT: Record<Trip['trip_kind'], string> = { flights: 'Flights', hotel: 'Hotel', both: 'Flights and hotel' }

export function TripNewPage() {
  const on = useTravelOn()
  const company = useCurrentCompany()
  const { me } = useMe()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [key] = useState(() => crypto.randomUUID())
  const [f, setF] = useState({
    origin: '', destination: '', trip_kind: 'both' as Trip['trip_kind'], depart_on: '', return_on: '', flexible: false,
    contact_phone: '', notes: '', max_points: '', team: false, prices: false, business: false,
  })
  const [names, setNames] = useState<string[]>([''])
  const set = (k: 'origin' | 'destination' | 'depart_on' | 'return_on' | 'contact_phone' | 'notes' | 'max_points') =>
    (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  const tick = (k: 'flexible' | 'team' | 'prices' | 'business') => (e: { target: { checked: boolean } }) => setF({ ...f, [k]: e.target.checked })
  const most = wholePoints(f.max_points)
  const filled = names.map((n) => n.trim()).filter(Boolean)
  const send = useMutation({
    mutationFn: () => api<Trip>('POST', `/api/companies/${company!.id}/trips`, {
      origin: f.origin, destination: f.destination, trip_kind: f.trip_kind, depart_on: f.depart_on,
      return_on: f.return_on || null, flexible_dates: f.flexible, traveler_names: filled,
      contact_email: me?.user?.email ?? '', contact_phone: f.contact_phone, notes: f.notes, max_points: most,
      consent_team: f.team, consent_prices: f.prices, business_travel: f.business, request_key: key,
    }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['travel'] })
      navigate(`/travel/${t.id}`)
    },
  })
  if (on === false) return <TravelOff />
  if (!company) return <NoCompany />
  const ready = f.origin.trim() !== '' && f.destination.trim() !== '' && f.depart_on !== '' && filled.length === names.length
    && f.contact_phone.trim() !== '' && most !== null && f.team && f.prices && f.business
  return (
    <div className="page stack" data-testid="travel-form">
      <PageBand title="Ask for a trip" sub="ernd sends a quote before anything is booked." />
      <form className="e-panel stack e-trip-form" onSubmit={(e) => { e.preventDefault(); if (ready) send.mutate() }}>
        <label>From<input data-testid="travel-origin" placeholder="City or airport" value={f.origin} onChange={set('origin')} maxLength={200} /></label>
        <label>To<input data-testid="travel-destination" placeholder="City or airport" value={f.destination} onChange={set('destination')} maxLength={200} /></label>
        <label>What to book
          <select data-testid="travel-kind" value={f.trip_kind} onChange={(e) => setF({ ...f, trip_kind: e.target.value as Trip['trip_kind'] })}>
            <option value="both">Flights and hotel</option>
            <option value="flights">Flights</option>
            <option value="hotel">Hotel</option>
          </select>
        </label>
        <label>Leave on<input type="date" data-testid="travel-depart" value={f.depart_on} onChange={set('depart_on')} /></label>
        <label>Back on (empty for one way)<input type="date" data-testid="travel-return" value={f.return_on} onChange={set('return_on')} /></label>
        <label><input type="checkbox" data-testid="travel-flexible" checked={f.flexible} onChange={tick('flexible')} />Dates can move 2 days either way</label>
        <div className="stack" data-testid="travel-names">
          {names.map((n, i) => (
            <label key={i}>{i === 0 ? 'Traveler name, as on their ID' : `Traveler ${i + 1}`}
              <input data-testid={`travel-name-${i}`} value={n} maxLength={120}
                onChange={(e) => setNames(names.map((x, j) => (j === i ? e.target.value : x)))} />
            </label>
          ))}
          <div className="row">
            {names.length < 20 && <button type="button" className="link-button" data-testid="travel-add-name" onClick={() => setNames([...names, ''])}>Add a traveler</button>}
            {names.length > 1 && <button type="button" className="link-button" onClick={() => setNames(names.slice(0, -1))}>Remove the last one</button>}
          </div>
        </div>
        <label>Phone for urgent changes<input type="tel" data-testid="travel-phone" value={f.contact_phone} onChange={set('contact_phone')} maxLength={40} /></label>
        <label>Notes<textarea data-testid="travel-notes" rows={3} placeholder="Times, cabin, bags, airlines to avoid, hotel area, budget a night" value={f.notes} onChange={set('notes')} maxLength={2000} /></label>
        <label>Most points to spend<input inputMode="numeric" data-testid="travel-max" placeholder="50,000" value={f.max_points} onChange={set('max_points')} /></label>
        {f.max_points !== '' && most === null && <p className="negative">Use a whole number, like 50,000.</p>}
        {most !== null && <p className="muted">Up to {cents(most)} of travel.</p>}
        <label><input type="checkbox" data-testid="travel-business" checked={f.business} onChange={tick('business')} />This trip is for company business.</label>
        <label><input type="checkbox" data-testid="travel-consent-team" checked={f.team} onChange={tick('team')} />Each traveler is on my company's team or is a guest of my company.</label>
        <label><input type="checkbox" data-testid="travel-consent-prices" checked={f.prices} onChange={tick('prices')} />Fares are not held until an admin accepts a quote, and the price is checked again before booking.</label>
        <p className="muted">Don't put passport numbers or birthdays in notes. If a booking needs them, we ask separately after the quote is accepted.</p>
        <div><button className="e-btn e-btn-primary e-btn-lg" type="submit" data-testid="travel-send" disabled={!ready || send.isPending}>Send request</button></div>
        {send.error && <ErrorBox error={send.error} />}
      </form>
    </div>
  )
}

export function TripPage() {
  const on = useTravelOn()
  const { id } = useParams()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['travel', 'trip', id],
    queryFn: () => api<Trip>('GET', `/api/trips/${id}`),
    enabled: on === true,
    refetchInterval: 10000,
  })
  const done = () => qc.invalidateQueries({ queryKey: ['travel'] })
  const accept = useMutation({
    mutationFn: (q: { points: number; version: number }) => api<Trip>('POST', `/api/trips/${id}/accept`, { points: q.points, quote_version: q.version }),
    onSuccess: done,
  })
  const limit = useMutation({ mutationFn: (pts: number) => api<Trip>('PUT', `/api/trips/${id}/limit`, { max_points: pts }), onSuccess: done })
  const cancel = useMutation({ mutationFn: () => api<Trip>('POST', `/api/trips/${id}/cancel`), onSuccess: done })
  if (on === false) return <TravelOff />
  if (q.isLoading || !q.data) return q.error ? <ErrorBox error={q.error} /> : <Loading />
  const t = q.data
  const busy = accept.isPending || limit.isPending || cancel.isPending
  const over = t.quote_points !== null && t.quote_points > t.max_points
  const expired = t.quote_expires_at !== null && new Date(t.quote_expires_at).getTime() <= Date.now()
  const short = t.quote_points !== null && (t.ready_points ?? 0) < t.quote_points
  const cancelButton = t.can_cancel && (
    <button className="e-btn" data-testid="trip-cancel" disabled={busy} onClick={() => cancel.mutate()}>Cancel trip</button>
  )
  return (
    <div className="page stack" data-testid="trip-page" data-status={t.status}>
      <PageBand title={t.destination} sub={`${dates(t)}${t.return_on ? '' : ', one way'}`} />
      <section className="e-panel">
      <dl className="grid">
        <div><dt className="muted">From</dt><dd>{t.origin}</dd></div>
        <div><dt className="muted">Booking</dt><dd>{KIND_TEXT[t.trip_kind]}</dd></div>
        <div><dt className="muted">Dates</dt><dd>{dates(t)}{t.return_on ? '' : ', one way'}{t.flexible_dates ? ', 2 days either way' : ''}</dd></div>
        <div><dt className="muted">Travelers</dt><dd>{(t.traveler_names ?? []).join(', ') || t.travelers}</dd></div>
        <div><dt className="muted">Most points</dt><dd className="num" data-testid="trip-max">{points(t.max_points)}</dd></div>
        <div><dt className="muted">State</dt><dd data-testid="trip-state">{TRIP_STATE[t.status] ?? t.status}</dd></div>
      </dl>
      {t.notes && <p className="muted" style={{ whiteSpace: 'pre-wrap', margin: '16px 0 0' }}>{t.notes}</p>}
      </section>

      {t.status === 'requested' && (
        <div className="e-panel stack">
          <p data-testid="trip-waiting">An email comes when the quote is ready.</p>
          {cancelButton && <div>{cancelButton}</div>}
        </div>
      )}

      {t.status === 'quoted' && t.quote_points !== null && t.cost_cents !== null && (
        <div className="e-panel stack e-quote" data-testid="trip-quote">
          <h2 className="e-heading">Quote</h2>
          <p className="e-quote-figure"><span className="num" data-testid="trip-points">{points(t.quote_points)}</span> points</p>
          <p style={{ whiteSpace: 'pre-wrap' }} data-testid="trip-itinerary">{t.itinerary}</p>
          <p className="muted">{cents(t.cost_cents)} with taxes and fees, as priced today. Points are held when an admin accepts and used once the trip is booked. If the price falls, fewer points are used.</p>
          <p className="muted" data-testid="trip-expires">{expired ? 'This quote has expired. Write to us for a new one.' : `Good until ${when(t.quote_expires_at)}.`}</p>
          {over && <p className="negative" data-testid="trip-over">This is more than the limit of {points(t.max_points)} points.</p>}
          {short && <p className="negative" data-testid="trip-short">Points ready: {points(t.ready_points ?? 0)}. This trip needs {points(t.quote_points)}.</p>}
          {t.can_accept ? (
            <div className="row">
              {over ? (
                <button className="e-btn e-btn-primary e-btn-lg" data-testid="trip-raise" disabled={busy} onClick={() => limit.mutate(t.quote_points!)}>
                  Raise the limit to {points(t.quote_points)}
                </button>
              ) : (
                <button className="e-btn e-btn-primary e-btn-lg" data-testid="trip-accept" disabled={busy || short || expired} onClick={() => accept.mutate({ points: t.quote_points!, version: t.quote_version ?? 1 })}>
                  Accept {points(t.quote_points)} points
                </button>
              )}
              {cancelButton}
            </div>
          ) : (
            <div className="stack">
              <p className="muted" data-testid="trip-admin-note">A company admin accepts this quote. We emailed them too.</p>
              {cancelButton && <div>{cancelButton}</div>}
            </div>
          )}
        </div>
      )}

      {t.status === 'accepted' && (
        <p className="e-panel" data-testid="trip-booking">Accepted. {points(t.quote_points ?? 0)} points are held while we book. To cancel, write to us.</p>
      )}
      {(t.status === 'booked' || t.status === 'refunded') && (
        <div className="e-panel stack" data-testid="trip-booked">
          <p>Booked for {cents(t.charged_cents ?? 0)}. {points(t.points_taken ?? 0)} points used.</p>
          {t.booking_reference && <p>Booking reference: {t.booking_reference}</p>}
          {t.itinerary && <p style={{ whiteSpace: 'pre-wrap' }}>{t.itinerary}</p>}
          {(t.refunds ?? []).map((r) => (
            <p key={r.id} data-testid="trip-refund">{r.form === 'credit'
              ? `The airline or hotel gave a credit of ${cents(r.refund_cents)} on ${day(r.created_at)}. We hold it for your company's next trip; no points come back for a credit.`
              : r.form === 'not_provided'
                ? `Part of the trip worth ${cents(r.refund_cents)} was not provided. ${points(r.points)} points came back on ${day(r.created_at)}.`
                : `The airline or hotel refunded ${cents(r.refund_cents)} on ${day(r.created_at)}. ${points(r.points)} points came back.`}</p>
          ))}
        </div>
      )}
      {t.status === 'declined' && <p className="e-panel" data-testid="trip-declined">Declined: {t.end_reason}</p>}
      {[accept.error, limit.error, cancel.error].filter(Boolean).map((e, i) => <ErrorBox key={i} error={e} />)}
      <p className="e-back"><Link className="e-link" to="/travel">All trips</Link></p>
    </div>
  )
}
